<template>
  <div class="session-edit-form">
    <!-- Edit mode indicator -->
    <div class="edit-mode-indicator">
      <div class="edit-badge">
        <span class="edit-icon">✏️</span>
        <span class="edit-text">Edit Mode</span>
      </div>
      <div class="dirty-indicator" v-if="isDirty" role="status" aria-live="polite">
        <span class="dirty-icon">●</span>
        <span class="dirty-text">Unsaved changes</span>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>Loading session data...</span>
    </div>
    
    <div v-else-if="error" class="error-state" role="alert">
      <span class="error-icon">⚠️</span>
      {{ error }}
    </div>
    
    <!-- Authentication Check - Show modal if not authenticated -->
    <div v-else-if="!authStore.isAuthValid" class="auth-step">
      <div class="form-section auth-section">
        <div class="section-header">
          <h2 class="section-title">🔐 Authentication Required</h2>
          <p class="section-description">Please authenticate to edit this session</p>
        </div>

        <div class="form-actions" style="justify-content:flex-start; margin-top: 1rem;">
          <button type="button" class="save-button" @click="handleAuthRequired">Authenticate</button>
        </div>
      </div>
    </div>
    
    <!-- Edit Form (only shown after authentication) -->
    <form v-else @submit.prevent="updateSession" class="edit-form">
      <!-- Basic Information Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">📅 Session Information</h2>
          <p class="section-description">Edit core session details</p>
        </div>
        
        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model="form.session_date"
              label="Session Date"
              type="date"
              placeholder=""
              :required="true"
              :error="getFieldError('session_date')"
              @validate="handleFieldValidation('session_date', $event)"
              @input="markDirty"
            />
          </div>
          <div class="form-col">
            <EnhancedFormField
              v-model="form.location"
              label="Location"
              type="text"
              placeholder="Enter session location"
              :required="true"
              :error="getFieldError('location')"
              @validate="handleFieldValidation('location', $event)"
              @input="markDirty"
            />
          </div>
        </div>
        
        <EnhancedFormField
          v-model="form.notes"
          label="Session Notes"
          type="textarea"
          placeholder="Add notes about this session..."
          :required="false"
          :disabled="false"
          :error="getFieldError('notes')"
          help-text="Optional notes for context or memories about this session"
          @validate="handleFieldValidation('notes', $event)"
          @input="markDirty"
        />
      </div>


      <!-- Form Actions -->
      <div class="form-actions">
        <button 
          type="button" 
          class="cancel-button"
          @click="handleCancel"
          :disabled="submitLoading"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          class="save-button"
          :disabled="!canSave"
          :class="{ loading: submitLoading }"
        >
          <span v-if="submitLoading" class="button-spinner"></span>
          {{ submitLoading ? 'Saving Changes...' : 'Save Changes' }}
        </button>
      </div>
      
      <!-- Status Messages -->
      <div v-if="success" class="status-message success-message" role="status">
        ✅ {{ success }}
      </div>
      <div v-if="submitError" class="status-message error-message" role="alert">
        ⚠️ {{ submitError }}
      </div>
    </form>

    <!-- Unsaved Changes Modal -->
    <div v-if="showUnsavedModal" class="modal-overlay" @click="closeUnsavedModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Unsaved Changes</h3>
        </div>
        <div class="modal-body">
          <p>You have unsaved changes. Are you sure you want to leave without saving?</p>
        </div>
        <div class="modal-actions">
          <button class="secondary-button" @click="closeUnsavedModal">Stay & Save</button>
          <button class="danger-button" @click="confirmLeave">Discard Changes</button>
        </div>
      </div>
    </div>

    <!-- Admin Authentication Modal -->
    <AdminAuthModal
      :visible="showAuthModal"
      @authenticated="onAuthenticated"
      @close="showAuthModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import EnhancedFormField from './EnhancedFormField.vue'
import AdminAuthModal from '../modals/AdminAuthModal.vue'
import { useFormValidation } from '../../composables/useFormValidation.js'
import { useAuthStore } from '../../stores/auth.js'
import apiService from '../../apiService.js'

// Session validation schema
const sessionValidationSchema = {
  session_date: {
    displayName: 'Session Date',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Session date is required'
    ]
  },
  location: {
    displayName: 'Location',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Location is required',
      (value) => value && value.length > 100 ? 'Location must be less than 100 characters' : null
    ]
  },
  notes: {
    displayName: 'Notes',
    rules: [
      (value) => value && value.length > 500 ? 'Notes must be less than 500 characters' : null
    ]
  },
  password: {
    displayName: 'Admin Password',
    rules: [
      (value) => value && value.trim() !== '' ? null : 'Admin password is required'
    ]
  }
}

export default {
  name: 'SessionEditForm',
  components: {
    EnhancedFormField,
    AdminAuthModal
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const router = useRouter()
    const authStore = useAuthStore()
    
    // Form validation
    const {
      validateForm,
      hasError,
      getFieldError,
      validateField,
      clearFieldError
    } = useFormValidation()
    
    // Form state
    const form = ref({
      session_date: '',
      location: '',
      notes: ''
    })
    
    const originalForm = ref({})
    const showAuthModal = ref(false)
    
    // UI state
    const loading = ref(true)
    const error = ref('')
    const submitLoading = ref(false)
    const submitError = ref('')
    const success = ref('')
    const isDirty = ref(false)
    const showUnsavedModal = ref(false)
    const pendingNavigation = ref(null)
    
    // Form validation
    const canSave = computed(() => {
      const validationData = { ...form.value }
      // Create a validation schema that excludes the password field since it's handled separately
      const editFormValidationSchema = { ...sessionValidationSchema }
      delete editFormValidationSchema.password
      
      const isValid = validateForm(validationData, editFormValidationSchema)
      // Allow saving if authenticated and form is valid
      return isValid && authStore.isAuthValid && !submitLoading.value
    })
    
    // Methods
    const handleAuthRequired = () => {
      if (!authStore.isAuthValid) {
        showAuthModal.value = true
      }
    }

    const onAuthenticated = () => {
      showAuthModal.value = false
      // Auth state now valid, form will become editable
    }
    
    const markDirty = () => {
      if (!isDirty.value) {
        isDirty.value = true
      }
    }
    
    const handleFieldValidation = (field, validationResult) => {
      // If validationResult is provided, use it to update the validation state
      if (validationResult) {
        if (!validationResult.isValid) {
          // Field has an error - the error is already set in the validation system
        } else {
          // Field is valid, clear any existing error
          clearFieldError(field)
        }
      } else {
        // If no validationResult provided, validate the field manually
        const value = form.value[field]
        
        const fieldRules = sessionValidationSchema[field]?.rules || []
        const result = validateField(field, value, fieldRules, sessionValidationSchema[field]?.displayName)
        
        if (!result.isValid) {
          // Error is already set by validateField
        } else {
          clearFieldError(field)
        }
      }
    }
    
    const loadSessionData = async () => {
      loading.value = true
      error.value = ''
      
      try {
        // Get session by ID - we need to use the WorkspaceSessions API to get session details
        const sessionsResponse = await apiService.WorkspaceSessions()
        const sessions = Array.isArray(sessionsResponse.data) ? sessionsResponse.data : sessionsResponse
        const session = sessions.find(s => s.id.toString() === props.id)
        
        if (!session) {
          error.value = 'Session not found'
          loading.value = false
          return
        }
        
        // Set form data with proper date formatting
        form.value = {
          session_date: session.session_date ? session.session_date.slice(0, 10) : '',
          location: session.location || '',
          notes: session.notes || ''
        }
        originalForm.value = { ...form.value }
        
        loading.value = false
      } catch (err) {
        console.error('Error loading session data:', err)
        error.value = 'Failed to load session data. Please try again.'
        loading.value = false
      }
    }
    
    const updateSession = async () => {
      // Create a validation schema that excludes the password field since it's handled separately
      const editFormValidationSchema = { ...sessionValidationSchema }
      delete editFormValidationSchema.password
      
      const isValid = validateForm(form.value, editFormValidationSchema)
      if (!isValid || !authStore.isAuthValid) {
        submitError.value = !authStore.isAuthValid ? 'Please authenticate with admin password first' : 'Please fix the errors above'
        return
      }

      submitLoading.value = true
      success.value = ''
      submitError.value = ''
      try {
        // Send all session fields including notes and admin password for authentication
        const sessionData = {
          session_date: form.value.session_date,
          location: form.value.location,
          notes: form.value.notes,
          password: authStore.adminPassword
        }
        await apiService.updateSession(props.id, sessionData)
        success.value = 'Session updated successfully!'
        isDirty.value = false
        originalForm.value = { ...form.value }
        setTimeout(() => { router.push('/manage-sessions') }, 2000)
      } catch (err) {
        submitError.value = err.message || 'Failed to update session. Please try again.'
      } finally {
        submitLoading.value = false
      }
    }
    
    const handleCancel = () => {
      if (isDirty.value) {
        showUnsavedModal.value = true
      } else {
        router.push('/manage-sessions')
      }
    }
    
    const closeUnsavedModal = () => {
      showUnsavedModal.value = false
      pendingNavigation.value = null
    }
    
    const confirmLeave = () => {
      isDirty.value = false
      showUnsavedModal.value = false
      
      if (pendingNavigation.value) {
        pendingNavigation.value()
      } else {
        router.push('/manage-sessions')
      }
    }
    
    // Watch for form changes to detect dirty state
    watch(form, () => {
      if (!loading.value) {
        const hasChanges = JSON.stringify(form.value) !== JSON.stringify(originalForm.value)
        if (hasChanges && !isDirty.value) {
          markDirty()
        }
      }
    }, { deep: true })
    
    // Navigation guard for unsaved changes
    onBeforeRouteLeave((to, from, next) => {
      if (isDirty.value) {
        showUnsavedModal.value = true
        pendingNavigation.value = next
      } else {
        next()
      }
    })
    
    // Browser unload protection
    const beforeUnloadHandler = (event) => {
      if (isDirty.value) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    
    onMounted(() => {
      loadSessionData()
      window.addEventListener('beforeunload', beforeUnloadHandler)
    })
    
    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    })
    
    return {
      form,
      authStore,
      showAuthModal,
      loading,
      error,
      submitLoading,
      submitError,
      success,
      isDirty,
      showUnsavedModal,
      canSave,
      markDirty,
      handleFieldValidation,
      updateSession,
      handleAuthRequired,
      onAuthenticated,
      handleCancel,
      closeUnsavedModal,
      confirmLeave,
      hasError,
      getFieldError
    }
  }
}
</script>

<style scoped>
/* Import the tier colors */
@import '../../styles/tier-colors.css';

.session-edit-form {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  color: #f3f3f3;
  position: relative;
}

/* Edit mode indicator */
.edit-mode-indicator {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(0, 191, 255, 0.1);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 8px;
}

.edit-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--tier-d-color);
  font-weight: 600;
}

.edit-icon {
  font-size: 1.25rem;
}

.dirty-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--tier-s-color);
  font-size: 0.875rem;
  font-weight: 500;
}

.dirty-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Loading and error states */
.loading-state,
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
}

.loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #444;
  border-top: 3px solid var(--tier-d-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.error-state {
  color: var(--tier-s-color);
  font-size: 1.125rem;
}

.error-icon {
  font-size: 1.5rem;
}

/* Form sections */
.form-section {
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.auth-section {
  background: rgba(255, 69, 0, 0.03);
  border-color: rgba(255, 69, 0, 0.1);
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #f3f3f3;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-description {
  font-size: 0.875rem;
  color: #aaa;
  margin: 0;
}

/* Form layout */
.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.form-col {
  flex: 1;
}

/* Form actions */
.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #404040;
}

/* Buttons */
.save-button,
.cancel-button {
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--tier-transition-fast);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  border: none;
}

.save-button {
  background: var(--tier-d-color);
  color: white;
}

.save-button:hover:not(:disabled) {
  background: var(--tier-d-light);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 191, 255, 0.3);
}

.save-button:disabled {
  background: #666;
  color: #999;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.cancel-button {
  background: transparent;
  color: #f3f3f3;
  border: 1px solid #666;
}

.cancel-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  border-color: #888;
}

.button-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Status messages */
.status-message {
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.success-message {
  background: rgba(50, 205, 50, 0.1);
  border: 1px solid rgba(50, 205, 50, 0.3);
  color: var(--tier-c-color);
}

.error-message {
  background: rgba(255, 69, 0, 0.1);
  border: 1px solid rgba(255, 69, 0, 0.3);
  color: var(--tier-s-color);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #2a2a2a;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.modal-header h3 {
  margin: 0 0 1rem 0;
  color: #f3f3f3;
  font-size: 1.25rem;
}

.modal-body p {
  margin: 0 0 1.5rem 0;
  color: #ccc;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.secondary-button,
.danger-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--tier-transition-fast);
  border: none;
}

.secondary-button {
  background: transparent;
  color: #f3f3f3;
  border: 1px solid #666;
}

.secondary-button:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: #888;
}

.danger-button {
  background: var(--tier-s-color);
  color: white;
}

.danger-button:hover {
  background: var(--tier-s-light);
  transform: translateY(-1px);
}

/* Responsive design */
@media (max-width: 768px) {
  .session-edit-form {
    padding: 1rem;
    margin: 1rem;
  }
  
  .edit-mode-indicator {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .form-actions {
    flex-direction: column-reverse;
    gap: 1rem;
  }
  
  .save-button,
  .cancel-button {
    width: 100%;
    justify-content: center;
  }
  
  .modal-content {
    margin: 1rem;
    padding: 1.5rem;
  }
  
  .modal-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .secondary-button,
  .danger-button {
    width: 100%;
    justify-content: center;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .form-section {
    border-width: 2px;
  }
  
  .save-button,
  .cancel-button {
    border-width: 2px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .edit-mode-indicator,
  .save-button,
  .cancel-button,
  .loading-spinner,
  .button-spinner {
    transition: none;
    animation: none;
  }
  
  .save-button:hover:not(:disabled),
  .danger-button:hover {
    transform: none;
  }
  
  .dirty-icon {
    animation: none;
  }
}
</style>